import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../lib/api';
import { swrLoad } from '../lib/swrCache';
import type { EventSeries, SeriesType, Venue } from '../types';
import SectionTabs from '../components/SectionTabs';
import { SERIES_TABS } from '../lib/section-tabs';

// Fix Leaflet's default marker icons broken by Vite's asset bundling.
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER: [number, number] = [30.3, -81.67];
const DEFAULT_ZOOM = 13;

const TYPE_LABELS: Record<SeriesType, string> = {
  'recurring-event': 'Recurring event',
  aggregator: 'Aggregator',
  organizer: 'Organizer',
};

interface MappedSeries {
  series: EventSeries;
  venue: Venue;
}

/**
 * Series map: plots active series at the location of their parent venue.
 * Series without a parent venue (or with a venue missing coordinates) are
 * shown in an "Unmapped series" sidebar so they remain reachable.
 */
export default function SeriesMapPage() {
  const [series, setSeries] = useState<EventSeries[] | null>(null);
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [typeFilter, setTypeFilter] = useState<'all' | SeriesType>('all');

  useEffect(() => {
    let cancelled = false;
    void swrLoad<EventSeries[]>({
      key: 'series:public',
      fetcher: () => api.get<EventSeries[]>('/api/series/public'),
      onData: (data) => setSeries(data),
      onError: () => setSeries([]),
      isCancelled: () => cancelled,
    });
    void swrLoad<Venue[]>({
      key: 'venues:public',
      fetcher: () => api.get<Venue[]>('/api/venues/public'),
      onData: (data) => setVenues(data),
      onError: () => setVenues([]),
      isCancelled: () => cancelled,
    });
    return () => { cancelled = true; };
  }, []);

  // Build a quick venue lookup once for both mapped + unmapped resolution.
  const venuesById = useMemo(() => {
    const map = new Map<string, Venue>();
    for (const v of venues ?? []) map.set(v.rowKey, v);
    return map;
  }, [venues]);

  const filtered = useMemo(() => {
    const list = series ?? [];
    return typeFilter === 'all' ? list : list.filter((s) => s.partitionKey === typeFilter);
  }, [series, typeFilter]);

  const { mapped, unmapped } = useMemo(() => {
    const mappedList: MappedSeries[] = [];
    const unmappedList: EventSeries[] = [];
    for (const s of filtered) {
      const venue = s.parentVenueId ? venuesById.get(s.parentVenueId) : undefined;
      if (venue && venue.lat != null && venue.lng != null) {
        mappedList.push({ series: s, venue });
      } else {
        unmappedList.push(s);
      }
    }
    mappedList.sort((a, b) => a.series.name.localeCompare(b.series.name));
    unmappedList.sort((a, b) => a.name.localeCompare(b.name));
    return { mapped: mappedList, unmapped: unmappedList };
  }, [filtered, venuesById]);

  const totalCount = filtered.length;
  const loading = series === null || venues === null;

  return (
    <SectionTabs ariaLabel="Series sections" items={SERIES_TABS}>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h1 className="mb-0">Series map</h1>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-sm-4 col-md-3">
          <label className="form-label small fw-semibold mb-1" htmlFor="series-map-filter-type">
            Type
          </label>
          <select
            id="series-map-filter-type"
            className="form-select form-select-sm"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as 'all' | SeriesType)}
          >
            <option value="all">All types</option>
            <option value="recurring-event">Recurring events</option>
            <option value="aggregator">Aggregators</option>
            <option value="organizer">Organizers</option>
          </select>
        </div>
      </div>

      <p className="text-muted small mb-3" aria-live="polite">
        {loading
          ? 'Loading…'
          : `${mapped.length} of ${totalCount} series pinned to a venue · ${unmapped.length} unmapped`}
      </p>

      <div className="row g-3">
        <div className="col-lg-9">
          <div className="mh-map-container rounded border overflow-hidden">
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {mapped.map(({ series: s, venue }) => (
                <Marker key={s.rowKey} position={[venue.lat as number, venue.lng as number]}>
                  <Popup>
                    <strong>
                      <Link to={`/series/${s.rowKey}`}>{s.name}</Link>
                    </strong>
                    <br />
                    <span className="text-muted" style={{ fontSize: '0.85em' }}>
                      {TYPE_LABELS[s.partitionKey]}
                    </span>
                    <br />
                    <span style={{ fontSize: '0.85em' }}>
                      at <Link to={`/venues/${venue.rowKey}`}>{venue.name}</Link>
                    </span>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>

        <aside className="col-lg-3" aria-labelledby="unmapped-series-heading">
          <div className="card">
            <div className="card-body">
              <h2 id="unmapped-series-heading" className="h6 mb-2">
                Unmapped series
              </h2>
              {!loading && unmapped.length === 0 && (
                <p className="text-muted small mb-0">All visible series are pinned to a venue.</p>
              )}
              {unmapped.length > 0 && (
                <ul className="list-unstyled small mb-0">
                  {unmapped.map((s) => (
                    <li key={s.rowKey} className="mb-2">
                      <Link to={`/series/${s.rowKey}`} className="fw-semibold">
                        {s.name}
                      </Link>
                      <div className="text-muted" style={{ fontSize: '0.85em' }}>
                        {TYPE_LABELS[s.partitionKey]}
                        {!s.parentVenueId && <> · no parent venue</>}
                        {s.parentVenueId && !venuesById.get(s.parentVenueId) && <> · venue unknown</>}
                        {s.parentVenueId
                          && venuesById.get(s.parentVenueId)
                          && (venuesById.get(s.parentVenueId)?.lat == null
                            || venuesById.get(s.parentVenueId)?.lng == null)
                          && <> · venue has no coordinates</>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </aside>
      </div>
    </SectionTabs>
  );
}
